/**
 * PlannedLayer Component
 *
 * Planned Layer 的入口組件，根據裝置類型切換：
 * - 桌面版：右側邊欄 (PlannedLayerSidebar)
 * - 行動版：底部托盤 (PlannedLayerTray)
 */
import React from 'react';
import { Task } from '../../../types/task';
import { logger } from '../../../utils/logger';
import PlannedLayerSidebar from './PlannedLayerSidebar';
import PlannedLayerTray from './PlannedLayerTray';

export interface PlannedLayerProps {
  /** 是否為行動版 */
  isMobile: boolean;
  /** 是否展開（僅行動版使用） */
  isExpanded?: boolean;
  /** 切換展開狀態（僅行動版使用） */
  onToggle?: () => void;
  /** 是否收合（桌面/平板版使用） */
  isCollapsed?: boolean;
  /** 切換收合狀態（桌面/平板版使用） */
  onToggleCollapse?: () => void;
  /** 拖曳開始回調（滑鼠） */
  onDragStart?: (task: Task, e: React.DragEvent) => void;
  /** 拖曳結束回調 */
  onDragEnd?: () => void;
  /** 觸控拖曳開始回調（平板） */
  onTouchStart?: (task: Task, e: React.TouchEvent) => void;
  /** 點擊查看任務詳情 */
  onViewTaskDetail?: (task: Task) => void;
  /** 快速排程回調（行動版 - 舊版） */
  onQuickSchedule?: (task: Task) => void;
  /** 進入放置模式回調（行動版 - 新版） */
  onEnterPlacementMode?: (task: Task) => void;
  /** 高亮指定 Task（從 TaskDetail Schedule 跳轉而來） */
  highlightTaskId?: string | null;
  /** 清除高亮回調 */
  onClearHighlight?: () => void;
}

const PlannedLayer: React.FC<PlannedLayerProps> = ({
  isMobile,
  isExpanded,
  onToggle,
  isCollapsed,
  onToggleCollapse,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onViewTaskDetail,
  onQuickSchedule,
  onEnterPlacementMode,
  highlightTaskId,
  onClearHighlight,
}) => {
  // 行動版：使用底部托盤（需要 isExpanded 和 onToggle）
  if (isMobile) {
    if (isExpanded === undefined || onToggle === undefined) {
      logger.warn('PlannedLayer: isExpanded and onToggle are required for mobile mode');
      return null;
    }
    return (
      <PlannedLayerTray
        isExpanded={isExpanded}
        onToggle={onToggle}
        onQuickSchedule={onQuickSchedule}
        onEnterPlacementMode={onEnterPlacementMode}
        onViewTaskDetail={onViewTaskDetail}
        highlightTaskId={highlightTaskId}
        onClearHighlight={onClearHighlight}
      />
    );
  }

  // 桌面版：使用右側邊欄（支援收合功能）
  return (
    <PlannedLayerSidebar
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onTouchStart={onTouchStart}
      onViewTaskDetail={onViewTaskDetail}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      highlightTaskId={highlightTaskId}
      onClearHighlight={onClearHighlight}
    />
  );
};

export default PlannedLayer;

// 導出子組件
export { default as PlannedLayerSidebar } from './PlannedLayerSidebar';
export { default as PlannedLayerTray } from './PlannedLayerTray';
export { default as PlannedTaskCard } from './PlannedTaskCard';
export { default as PlannedTaskGroup } from './PlannedTaskGroup';
